import { AudienceType, CommunicationAudienceType, SelectedStartType } from "../../config/enums";
import { validateAutomationAudienceRunAt } from "./communicationAudienceValidation";
import { FieldBehaviour } from "../../config/fieldBehaviours";
import { FieldMinDate } from "../../config/fieldMinDates";
import { FieldValidator } from "../../config/fieldValidators";
import { LookupOptionFilter } from "../../config/lookupOptionFilters";
import { VisibilityRule } from "../../config/visibilityRules";
import { hasSupportedCommunicationTestingControlInState } from "./testingControlDetection";
import { lookupRecordsCache } from "../../hooks/lookupRecords";
import { communicationEntityName } from "./communicationOptions";

type Rec = Record<string, unknown>;

// The same patterns as js/utility's, which drags the service layer in.
export const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const startOfDay = (value: unknown) => {
  const date = new Date(value as string);

  date.setHours(0, 0, 0, 0);
  return date;
};

// Dates are compared by day, and nothing set is later than anything.
const isSameOrLaterDate = (date: unknown, other: unknown) =>
  !date || !other || startOfDay(date) >= startOfDay(other);

const is = (value: unknown, expected: string | number) =>
  String(value ?? "") === String(expected);

/** A communication sent by an automation takes its timing from the automation. */
export const isAutomationExecution = (record: Rec) => Boolean(record.AutomationExecutionId);

export const isPublished = (record: Rec) => Boolean(record.g4c_sid);

const isActivated = (record: Rec) => Boolean(record.g4c_customerid);

const isABTest = (record: Rec) => is(record.g4c_audiencetype, AudienceType["A/B Testing"]);

const isExistingAudience = (record: Rec) => is(record.g4c_audiencetype, AudienceType.Existing);

const offersABTesting = (record: Rec, config: Rec) =>
  Boolean(config.ABTestingEnabled) &&
  is(record.g4c_communicationtype, CommunicationAudienceType.Marketing);

const showsTargetedAt = (record: Rec) => !isABTest(record);

const showsStartType =(record: Rec) =>
  isAutomationExecution(record) ||
  isExistingAudience(record) ||
  (isABTest(record) && isPublished(record));

const showsEndDate = (record: Rec) => !isABTest(record) || isPublished(record);

// A number the radio chose and the text a saved record holds are the same choice.
const sameValue = (a: unknown, b: unknown) => String(a ?? "") === String(b ?? "");

const runAtLabel = (value: unknown) => (String(value).toLowerCase() === "warehouse" ? "Warehouse" : "CRM");

/**
 * Whether the existing audience chosen runs where the communication does; nothing until
 * there is an audience to check, or the audience's own run-at is known.
 */
export function audienceRunAtCheck(record: Rec): { ok: boolean; message: string } | null {
  const audience = isExistingAudience(record) && record.g4c_audienceid
    ? (lookupRecordsCache.g4c_communicationaudience as { Key: unknown; RunAt?: unknown }[] | undefined)?.find((option) => sameValue(option.Key, record.g4c_audienceid))
    : undefined;

  if (!audience || audience.RunAt == null || record.RunAt == null) {
    return null;
  }

  return sameValue(audience.RunAt, record.RunAt)
    ? { ok: true, message: `The audience runs at ${runAtLabel(audience.RunAt)}, matching this communication.` }
    : {
        ok: false,
        message: `The audience runs at ${runAtLabel(audience.RunAt)}, but this communication runs at ${runAtLabel(record.RunAt)}. Choose an audience that runs at ${runAtLabel(record.RunAt)}.`,
      };
}

export const communicationVisibilityRules: Record<string, VisibilityRule> = {
  communicationLayoutStep: (record) => isActivated(record) && !record.id,
  communicationNotActivated: (record) => !isActivated(record),
  communicationDraft: (record) => isActivated(record) && !isPublished(record),
  communicationPublished: (record) => isActivated(record) && isPublished(record),
  communicationRunAtDraft: (record, config) =>
    isActivated(record) && !isPublished(record) && Boolean(config.WarehouseCommunicationAudienceEnabled),
  communicationRunAtPublished: (record, config) =>
    isActivated(record) && isPublished(record) && Boolean(config.WarehouseCommunicationAudienceEnabled),
  communicationDynamicContentDraft: (record, config) =>
    isActivated(record) && !isPublished(record) && Boolean(config.DynamicContentEnabled),
  communicationDynamicContentPublished: (record, config) =>
    isActivated(record) && isPublished(record) && Boolean(config.DynamicContentEnabled),
  communicationAudienceWithAB: offersABTesting,
  communicationAudienceWithoutAB: (record, config) => !offersABTesting(record, config),
  communicationTargetedAt: showsTargetedAt,
  communicationTargetedAtChoice: (record) =>
    showsTargetedAt(record) && !record.id && !record.g4c_subject,
  communicationTargetedAtFixed: (record) =>
    showsTargetedAt(record) && Boolean(record.id || record.g4c_subject),
  communicationExistingAudience: isExistingAudience,
  communicationHasBasket: (record) => hasSupportedCommunicationTestingControlInState(record),
  communicationABTestNotice: (record) => isActivated(record) && !isPublished(record) && isABTest(record),
  communicationAudienceSummary: (record) => isExistingAudience(record) && Boolean(record.g4c_audienceid),
  communicationStartType: showsStartType,
  communicationStartDate: (record) =>
    is(record.g4c_starttype, SelectedStartType.Scheduled) ||
    (isPublished(record) && (isABTest(record) || Boolean(record.g4c_startdate))),
  communicationEndDate: showsEndDate,
  communicationAudienceTypeFixed: (record) => Boolean(record.id),
};

const laterThanStart = (date: unknown, record: Rec) =>
  is(record.g4c_starttype, SelectedStartType.Scheduled) && record.g4c_startdate
    ? isSameOrLaterDate(date, new Date(record.g4c_startdate as string))
    : is(record.g4c_starttype, SelectedStartType.Immediately)
    ? isSameOrLaterDate(date, new Date())
    : true;

export const communicationValidators: Record<string, FieldValidator> = {
  // A searchable lookup has no native control for the browser's required check to read.
  communicationRequiredLookup: (value) =>
    value === null || value === undefined || value === "" ? "This field is required" : "",
  communicationAudienceLookup: (value, record) =>
    value === null || value === undefined || value === ""
      ? "This field is required"
      : audienceRunAtCheck(record)?.ok === false
      ? audienceRunAtCheck(record)?.message ?? ""
      : "",
  communicationLayoutRequired: (value) => (value ? "" : "Choose a layout to start the email from"),
  communicationFromAddress: (email) =>
    email && isValidEmail(email as string) ? "" : "Please enter a valid email address",
  communicationTestAddress: (email) =>
    email && !isValidEmail(email as string) ? "Please enter a valid email address" : "",
  communicationAudienceType: (value, record) =>
    validateAutomationAudienceRunAt(value, record, record, communicationEntityName),
  communicationEndDate: (date, record) =>
    record.g4c_enddate && !laterThanStart(date, record) ? "Select a later date" : "",
};

/** The end date can't be before the start date, or before today when sent immediately. */
export const communicationMinDates: Record<string, FieldMinDate> = {
  communicationEndDate: (record) =>
    is(record.g4c_starttype, SelectedStartType.Scheduled) && record.g4c_startdate
      ? new Date(record.g4c_startdate as string)
      : is(record.g4c_starttype, SelectedStartType.Immediately)
      ? new Date()
      : undefined,
};

/** Choosing the audience type sets when to send, as the legacy Publish stage did. */
export const communicationBehaviours: Record<string, FieldBehaviour> = {
  communicationStartTypeFromAudience: (value, record) =>
    isAutomationExecution(record)
      ? record.g4c_starttype
      : is(value, AudienceType.Existing)
      ? SelectedStartType.Immediately
      : "",
};

/** An existing audience has to be the communication's type and run where it runs. */
export const communicationOptionFilters: Record<string, LookupOptionFilter> = {
  communicationAudience: (options, record) =>
    options.filter(
      (option) =>
        sameValue(option.Type, record.g4c_communicationtype) &&
        sameValue(option.RunAt, record.RunAt)
    ),
};
